/**
 * Module dependencies.
 */

import { Button } from 'app/components/atoms/button/button';
import { CrudEdit } from 'app/components/organisms/crud/crud-edit';
import { Drawer } from 'app/components/organisms/drawer/drawer';
import { createTransactionsTemplate } from 'app/components/pages/bank-accounts/movements/movements-config';
import { Pencil } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

/**
 * `Props` type.
 */

type Props = {
  form: UseFormReturn<FieldValues, any>;
  formType: 'add' | 'edit';
};

/**
 * Export `EditTransactionAction` component.
 *
 * Rendered inside the project-movement edit form: when the movement is linked
 * to a bank transaction, it opens that transaction's edit form in a drawer,
 * reusing the bank-accounts movements template.
 */

export function EditTransactionAction({ form, formType }: Props): JSX.Element | null {
  const [translate] = useTranslation();
  const [open, setOpen] = useState(false);
  const transactionId = form.watch('transactionId');
  const template = useMemo(() => createTransactionsTemplate(translate), [translate]);

  // Only offer this on an existing movement that has a linked transaction.
  if (formType !== 'edit' || transactionId == null || transactionId === '') {
    return null;
  }

  // Mirror the select's layout (input height, then a reserved error row, then
  // the FormField's `pb-5`) so the row's `align-items: flex-end` lines the icon
  // up with the input beside it.
  return (
    <div className={'shrink-0 pb-5'}>
      <Button
        aria-label={translate('common.labels.editTargetTransaction')}
        className={'h-10 w-10'}
        icon={<Pencil className={'h-4 w-4'} />}
        onClick={() => setOpen(true)}
        title={translate('common.labels.editTargetTransaction')}
        type={'default'}
      />

      <span aria-hidden className={'block min-h-[1.125rem] pt-1'} />

      <Drawer
        isOpen={open}
        onClose={() => setOpen(false)}
        title={translate('common.labels.editTargetTransaction')}
        width={780}
      >
        {open ? <CrudEdit id={transactionId} onDone={() => setOpen(false)} template={template} /> : <span />}
      </Drawer>
    </div>
  );
}
